"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import CitySelectionPopup from "../_components/global/CitySelectionPopup";
import { CITIES, LIVE_CITY_ID } from "../_constants/cities";
import { SELECTED_CITY_STORAGE_KEY, getStoredCityId } from "../_constants/selectedCity";
import { fevicolInhomzRootRoute } from "../_constants/tempRootRoute";
import { inhomzEvents } from "../_lib/events";
import { useInhomzTrack } from "../_hooks/useInhomzTrack";
const CityPopupContext = createContext(null);

// Keyed by the city it was shown for, not just a boolean — if LIVE_CITY_ID
// ever changes (next city goes live), the stale value won't match and the
// notice correctly shows again.
const CITY_NOTICE_SEEN_STORAGE_KEY = "fevicolInhomzCityNoticeSeenFor";

// A separate concern from the notice-seen flag above: this is the city the
// shopper actually confirmed via the popup's "Continue" button, read lazily
// (event-handler time, never during render) so there's no SSR/hydration
// mismatch to manage for it at all.
function setStoredCityId(cityId) {
  try {
    localStorage.setItem(SELECTED_CITY_STORAGE_KEY, cityId);
  } catch {
    // Private browsing / storage disabled — the redirect-on-reopen below is
    // a convenience, not something the popup flow depends on to function.
  }
}

// Three states, not a boolean: a consumer branching on "has a city been
// selected" needs to tell "don't know yet" apart from "confirmed no" — the
// localStorage read can't happen before mount, so there's a real gap where
// neither is true yet. Collapsing that into a boolean forces a default guess
// (almost always "no"), which is exactly what flashed the wrong section for
// returning visitors before this was split out.
export const CITY_SELECTION_STATUS = {
  CHECKING: "checking",
  SELECTED: "selected",
  UNSELECTED: "unselected"
};

/**
 * Wrap any fevicol-inhomz page section tree with this to make the city
 * popup openable from anywhere inside it via `useCityPopup()`.
 *
 * The service is only live in one city right now (LIVE_CITY_ID), so there's
 * nothing to actually select yet — `selectedCity` is a static constant, not
 * state. The popup itself is just an informational "we're only live in X"
 * notice, but whether a visitor has already seen *that* notice does persist
 * to localStorage (keyed to LIVE_CITY_ID, see `CITY_NOTICE_SEEN_STORAGE_KEY`)
 * so `autoOpen` only fires once per city rather than on every visit.
 *
 * Whether the notice has been seen isn't known until after mount (it lives
 * in localStorage, unavailable during SSR), so `autoOpen` always seeds
 * closed and the mount effect opens it if localStorage says otherwise —
 * deciding eagerly from `typeof window` would make the server and client's
 * first render disagree (hydration mismatch), the same issue the old
 * multi-city version hit (see git history, "fix city popup hydration").
 *
 * Whether a "browse/book a professional" trigger (sidebar, header, homepage/
 * design-ideas CTAs) skips the popup is decided per-open, not per-page:
 * `openCityPopup()` checks `SELECTED_CITY_STORAGE_KEY` first — a city already
 * confirmed via a previous "Continue" click redirects straight to that
 * city's contractors listing with no popup at all; nothing stored yet opens
 * the popup. (Single-city today, so confirming it once makes every later
 * click redirect straight through — this is the plumbing for when more
 * cities go live, not a visible change yet.)
 *
 * Two kinds of callers opt out of that stored-city shortcut via
 * `{ changeCity: true }`, which always opens the popup regardless of what's
 * stored: a persistent "city" row that shouldn't silently redirect away from
 * wherever the shopper currently is (header, sidebar's "Select City", the
 * design-idea detail page's "best matches" dock — these also pass
 * `redirectOnContinue: false` so confirming doesn't navigate either), and a
 * listing page's own city switcher (`ContractorsListSummary`, where
 * confirming a — eventually different — city *should* navigate, so it keeps
 * the `redirectOnContinue` default of `true`). The homepage's auto-open-on-
 * load prompt is the other `redirectOnContinue: false` case, but doesn't go
 * through `openCityPopup()` at all — it's seeded directly by the mount
 * effect below.
 *
 * `CITIES` still lists every future city with its label/icon so this
 * provider and popup need no rewiring when multi-city selection comes back —
 * only `LIVE_CITY_ID` and the persistence need to change.
 */
export function CityPopupProvider({
  children,
  autoOpen = false
}) {
  const router = useRouter();
  const track = useInhomzTrack();
  const hasTrackedView = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [redirectOnContinue, setRedirectOnContinue] = useState(!autoOpen);
  // Whether a city has been *confirmed* (not just the single live city
  // existing) — seeds CHECKING for both server and first client render (no
  // localStorage access possible yet) and resolves to SELECTED/UNSELECTED in
  // the mount effect below, same hydration-safety pattern as the notice-seen
  // check. Consumers (e.g. the design-idea detail page) use this to decide
  // between a loading skeleton, real contractor matches, or a "pick a city
  // first" prompt.
  const [citySelectionStatus, setCitySelectionStatus] = useState(CITY_SELECTION_STATUS.CHECKING);
  useEffect(() => {
    // The auto-open prompt saves the city on first load, not on a CTA click.
    if (autoOpen && !getStoredCityId()) setStoredCityId(LIVE_CITY_ID);
    setCitySelectionStatus(getStoredCityId() ? CITY_SELECTION_STATUS.SELECTED : CITY_SELECTION_STATUS.UNSELECTED);
    // Only ever meant to run once, against the localStorage value present on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!autoOpen) return;
    const seenForCityId = localStorage.getItem(CITY_NOTICE_SEEN_STORAGE_KEY);
    if (seenForCityId !== LIVE_CITY_ID) {
      setIsOpen(true);
      // Mark it seen on open, not on a CTA click — backdrop/Escape dismissals
      // go through closeCityPopup and would otherwise re-open it every load.
      markCityNoticeSeen();
    }
    // Only ever meant to run once, against the localStorage value present on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-arms on every close so each popup showing counts once.
  useEffect(() => {
    if (!isOpen) {
      hasTrackedView.current = false;
      return;
    }
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    track(inhomzEvents.cityPopup.VIEWED, {
      redirect_on_continue: redirectOnContinue
    });
  });
  const selectedCity = CITIES.find(city => city.id === LIVE_CITY_ID) ?? CITIES[0];
  const markCityNoticeSeen = () => {
    localStorage.setItem(CITY_NOTICE_SEEN_STORAGE_KEY, LIVE_CITY_ID);
  };
  const openCityPopup = ({
    redirectOnContinue: shouldRedirect = true,
    changeCity = false
  } = {}) => {
    if (!changeCity) {
      const storedCityId = getStoredCityId();
      if (storedCityId) {
        router.push(`${fevicolInhomzRootRoute}/find-contractors/${storedCityId}`);
        return;
      }
    }
    setRedirectOnContinue(shouldRedirect);
    setIsOpen(true);
  };
  const closeCityPopup = () => setIsOpen(false);
  const handleContinue = () => {
    track(inhomzEvents.cityPopup.CONTINUE_CLICKED, {
      event_value: selectedCity.id
    });
    setStoredCityId(selectedCity.id);
    setCitySelectionStatus(CITY_SELECTION_STATUS.SELECTED);
    markCityNoticeSeen();
    closeCityPopup();
    if (redirectOnContinue) {
      router.push(`${fevicolInhomzRootRoute}/find-contractors/${selectedCity.id}`);
    }
  };
  const handleBrowseDesigns = () => {
    track(inhomzEvents.cityPopup.BROWSE_IDEAS_CLICKED);
    markCityNoticeSeen();
    closeCityPopup();
    router.push(`${fevicolInhomzRootRoute}/design-ideas`);
  };
  return <CityPopupContext.Provider value={{
    openCityPopup,
    selectedCity,
    citySelectionStatus
  }}>
      {children}
      <CitySelectionPopup open={isOpen} cityLabel={selectedCity.label} cityIconSrc={selectedCity.iconSrc} onClose={closeCityPopup} onContinue={handleContinue} onBrowseDesigns={handleBrowseDesigns} />
    </CityPopupContext.Provider>;
}
export function useCityPopup() {
  const context = useContext(CityPopupContext);
  if (!context) {
    throw new Error("useCityPopup must be used within a CityPopupProvider");
  }
  return context;
}